import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { readDeviceConfig, writeDeviceConfig } from './ble';
import { BASE_URL } from '../v2Api';
import ProtobufObjectTable from './ProtobufObjectTable';
import protobuf from 'protobufjs';

const DeviceConfig = forwardRef(({ isConnected }, ref) => {
  const [deviceConfig, setDeviceConfig] = useState({});
  const [cloudDeviceConfig, setCloudDeviceConfig] = useState({});
  const [deviceConfigError, setDeviceConfigError] = useState(null);
  const [differences, setDifferences] = useState([]);
  const [protobufRoot, setProtobufRoot] = useState(null);
  const [applyChangesAutomatically, setApplyChangesAutomatically] = useState(false);

  const setDefaultValues = (obj, type) => {
    const defaultValues = {};
    const fields = type.fields;
    for (const key in fields) {
      const fieldType = fields[key]?.resolvedType;
      if (fieldType && fieldType instanceof protobuf.Type) {
        defaultValues[key] = setDefaultValues({}, fieldType);
      } else {
        switch (fields[key].type) {
          case 'bool':
            defaultValues[key] = false;
            break;
          case 'int32':
          case 'uint32':
          case 'float':
          case 'double':
            defaultValues[key] = 0;
            break;
          case 'string':
            defaultValues[key] = '';
            break;
          case 'GPSModel':
            defaultValues[key] = 0; // Default to the first enum value
            break;
          default:
            defaultValues[key] = null;
        }
      }
    }
    return { ...defaultValues, ...obj };
  };

  const getEnumName = (type, value) => {
    const enumObj = protobufRoot.lookupEnum(type);
    return enumObj ? enumObj.valuesById[value] || value : value;
  };

  const roundToThreeDecimals = (num) => {
    return Math.round(num * 1000) / 1000;
  };

  const compareArrays = (localArray, cloudArray, type, path) => {
    const diffs = [];
    const maxLength = Math.max(localArray.length, cloudArray.length);

    for (let i = 0; i < maxLength; i++) {
      const localElement = localArray[i];
      const cloudElement = cloudArray[i];
      const currentPath = `${path}[${i}]`;

      if (Array.isArray(localElement) && Array.isArray(cloudElement)) {
        diffs.push(...compareArrays(localElement, cloudElement, type, currentPath));
      } else if (typeof localElement === 'object' && typeof cloudElement === 'object') {
        diffs.push(...compareConfigs(localElement, cloudElement, type, currentPath));
      } else if (typeof localElement === 'number' && typeof cloudElement === 'number') {
        if (roundToThreeDecimals(localElement) !== roundToThreeDecimals(cloudElement)) {
          diffs.push({ field: currentPath, local: roundToThreeDecimals(localElement), cloud: roundToThreeDecimals(cloudElement) });
        }
      } else if (localElement !== cloudElement) {
        diffs.push({ field: currentPath, local: localElement, cloud: cloudElement });
      }
    }

    return diffs;
  };

  const compareConfigs = (localConfig, cloudConfig, type, path = '') => {
    if (!localConfig || !cloudConfig) { // no data yet
      return [];
    }

    const diffs = [];
    const fields = type.fields;

    for (const key in fields) {
      if (key === '$type') continue; // Ignore $type
      const fieldType = fields[key]?.resolvedType;
      const currentPath = path ? `${path}.${key}` : key;

      let enumType = null;
      try {
        enumType = protobufRoot.lookupEnum(fields[key].type);
      } catch (e) { }

      if (Array.isArray(localConfig[key]) && Array.isArray(cloudConfig[key])) {
        diffs.push(...compareArrays(localConfig[key], cloudConfig[key], fieldType, currentPath));
      } else if (fieldType && fieldType instanceof protobuf.Type) {
        const nestedLocalConfig = localConfig[key] || {};
        const nestedCloudConfig = cloudConfig[key] || {};
        const nestedDiffs = compareConfigs(nestedLocalConfig, nestedCloudConfig, fieldType, currentPath);
        diffs.push(...nestedDiffs);
      } else if (enumType) {
        const localEnum = getEnumName(fields[key].type, localConfig[key]);
        const cloudEnum = getEnumName(fields[key].type, cloudConfig[key]);
        if (localEnum !== cloudEnum) {
          diffs.push({ field: currentPath, local: localEnum, cloud: cloudEnum });
        }
      } else if ((fields[key].type === 'float' || fields[key].type === 'double') &&
        (localConfig[key] !== undefined && cloudConfig[key] !== undefined)) {
        if (roundToThreeDecimals(localConfig[key]) !== roundToThreeDecimals(cloudConfig[key])) {
          diffs.push({ field: currentPath, local: roundToThreeDecimals(localConfig[key]), cloud: roundToThreeDecimals(cloudConfig[key]) });
        }
      } else if (localConfig[key] !== cloudConfig[key]) {
        diffs.push({ field: currentPath, local: localConfig[key], cloud: cloudConfig[key] });
      }
    }

    return diffs;
  };

  const fetchCloudDeviceConfig = async (config, overwrite = false) => {
    try {
      const response = await fetch(`${BASE_URL}/device_config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deviceConfig: config, overwrite }),
      });

      if (!response.ok) {
        throw new Error(`Error fetching cloud config: ${response.statusText}`);
      }

      const cloudResponse = await response.json();
      return cloudResponse.deviceConfig;
    } catch (error) {
      setDeviceConfigError(`Failed to fetch cloud config: ${error.message}`);
      return null;
    }
  };

  const updateDeviceConfig = async (updatedConfig, overwrite) => {
    try {
      await writeDeviceConfig(updatedConfig);

      const cloudConfig = await fetchCloudDeviceConfig(setDefaultValues(updatedConfig, protobufRoot.lookupType('compass.DeviceConfig')), overwrite);

      if (cloudConfig) {
        const CompassDeviceConfig = protobufRoot.lookupType('compass.DeviceConfig');
        const diffs = compareConfigs(updatedConfig, cloudConfig, CompassDeviceConfig);
        setCloudDeviceConfig(cloudConfig);
        setDifferences(diffs);
      }
    } catch (error) {
      alert('Failed to update device configuration');
    }
  };

  const fetchDeviceConfig = async () => {
    try {
      const CompassDeviceConfig = protobufRoot.lookupType('compass.DeviceConfig');
      const defaults = setDefaultValues({}, CompassDeviceConfig);
      const config = await readDeviceConfig();
      const configWithDefaults = { ...defaults, ...config }; // Apply read config over defaults
      setDeviceConfig(configWithDefaults);
      setDeviceConfigError(null);
    } catch (error) {
      setDeviceConfigError('Error reading device configuration.');
      setDeviceConfig({});
    }
  };

  useEffect(() => {
    const loadProtobuf = async () => {
      const root = await protobuf.load('/compassData.proto');
      setProtobufRoot(root);
    };

    loadProtobuf();
  }, []);

  useEffect(() => {
    if (protobufRoot && isConnected) {
      fetchDeviceConfig();
    }
  }, [protobufRoot, isConnected]);

  useEffect(() => {
    const fetchAndCompareConfigs = async () => {
      if (!protobufRoot) return;
      const CompassDeviceConfig = protobufRoot.lookupType('compass.DeviceConfig');
      const cloudConfig = await fetchCloudDeviceConfig(setDefaultValues(deviceConfig, CompassDeviceConfig), false);

      if (cloudConfig) {
        const diffs = compareConfigs(deviceConfig, cloudConfig, CompassDeviceConfig);
        setCloudDeviceConfig(cloudConfig);
        setDifferences(diffs);
      }
    };

    fetchAndCompareConfigs();
  }, [deviceConfig, protobufRoot]);

  const handleConfigChange = (key, value) => {
    const updatedConfig = {
      ...deviceConfig,
      [key]: value,
    };
    setDeviceConfig(updatedConfig);
    if (applyChangesAutomatically) {
      writeDeviceConfig(updatedConfig);
    }
  };

  const handleSaveConfig = async () => {
    await updateDeviceConfig(deviceConfig, true);
  };

  const handleReloadConfig = async () => {
    await fetchDeviceConfig();
  };

  const handleUpdateFromCloud = async () => {
    await updateDeviceConfig(cloudDeviceConfig, true);
  };

  const handleCheckboxChange = () => {
    setApplyChangesAutomatically(!applyChangesAutomatically);
  };

  useImperativeHandle(ref, () => ({
    handleSaveConfig,
    getDeviceConfig: () => deviceConfig,
    setDeviceConfig,
  }));

  const renderDifferences = (differences) => (
    differences.map((diff, index) => (
      <tr key={index}>
        <td>{diff.field}</td>
        <td>{diff.local !== undefined ? JSON.stringify(diff.local) : 'undefined'}</td>
        <td>{diff.cloud !== undefined ? JSON.stringify(diff.cloud) : 'undefined'}</td>
      </tr>
    ))
  );

  return (
    <div className="table-container">
      <h2>Device Config</h2>
          {protobufRoot && (
            <>
              <ProtobufObjectTable
                object={deviceConfig}
                typeName="compass.DeviceConfig"
                protobufRoot={protobufRoot}
                editable={true}
                onChange={handleConfigChange}
                readOnlyFields={['bleMac']}
              />
              <div>
                <label>
                  <input
                    type="checkbox"
                    checked={applyChangesAutomatically}
                    onChange={handleCheckboxChange}
                  />
                  Apply changes automatically
                </label>
              </div>
              <button onClick={handleSaveConfig}>Save Device Config</button>
              <button onClick={handleReloadConfig}>Reload Config</button>
            </>
          )}

      {deviceConfigError ? (
        <div className="error-message">{deviceConfigError}</div>
      ) : (
        differences.length > 0 ? (
          <>
            <div className="differences">
              <h3>Differences with Cloud</h3>
              <table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Local</th>
                    <th>Cloud</th>
                  </tr>
                </thead>
                <tbody>{renderDifferences(differences)}</tbody>
              </table>
              <button onClick={handleUpdateFromCloud}>Update Device using config from cloud</button>
              <button onClick={() => updateDeviceConfig(deviceConfig, true)}>Upload device config to cloud</button>
            </div>
          </>
        ) : (
          <div>Compass config is registered in the cloud and up-to-date</div>
        )
      )}
    </div>
  );
});

export default DeviceConfig;
